import type {
  PeerDagData,
  ToolCallDagData,
  LlmRoundData,
  TurnMarkerData,
} from '../types';

// v0.4 Task 12: per-kind ARIA labels for DAG nodes. Each label
// reads as a single announceable phrase that surfaces persona
// identity + status + headline telemetry (cost/duration/tokens).
// Screen reader users hear "Peer response from X, ok, 420 tokens,
// $0.003, 2.3s" — the same at-a-glance scan a sighted user gets
// from the rich card.

export function peerAriaLabel(d: PeerDagData): string {
  const parts: string[] = [`Peer response from ${d.personaName ?? 'unknown'}`];
  if (d.status) parts.push(d.status);
  if (d.totalTokens != null) parts.push(`${d.totalTokens} tokens`);
  if (d.costUsd != null) parts.push(`$${d.costUsd.toFixed(4)}`);
  if (d.durationMs != null)
    parts.push(`${(d.durationMs / 1000).toFixed(1)}s`);
  return parts.join(', ');
}

export function toolAriaLabel(d: ToolCallDagData): string {
  const name =
    d.call.identity?.tool_name ?? (d.call as any).tool_name ?? 'tool';
  const status = d.call.outcome?.status ?? 'ok';
  const dur = d.call.timing?.duration_ms ?? null;
  const parts: string[] = [`Tool call ${name}`, `round ${d.round}`, status];
  if (dur != null) parts.push(`${(dur / 1000).toFixed(1)}s`);
  return parts.join(', ');
}

export function roundAriaLabel(d: LlmRoundData): string {
  const parts: string[] = [
    `${d.isSynthesis ? 'Synthesis' : 'LLM round'} ${d.round}`,
  ];
  if (d.modelUsed) parts.push(d.modelUsed.split('/').pop() ?? d.modelUsed);
  if (d.toolCallCount > 0)
    parts.push(`${d.toolCallCount} tool${d.toolCallCount === 1 ? '' : 's'}`);
  return parts.join(', ');
}

export function turnMarkerAriaLabel(d: TurnMarkerData): string {
  if (d.role === 'user') {
    const preview = (d.fullContent ?? d.preview ?? '').slice(0, 80);
    return `User prompt: ${preview}`;
  }
  const parts: string[] = ['Assistant answer'];
  if (d.totalCostUsd != null) parts.push(`$${d.totalCostUsd.toFixed(4)}`);
  if (d.totalTokens != null) parts.push(`${d.totalTokens} tokens`);
  if (d.toolCallCount)
    parts.push(
      `${d.toolCallCount} tool call${d.toolCallCount === 1 ? '' : 's'}`,
    );
  return parts.join(', ');
}
